// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the reproducibility bundle (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * §Next S8): one download with everything a reader needs to check and
 * redo what was shown: the citation (text, BibTeX, RIS), the link that
 * reopens the view, the methods paragraph with its references, every CSV
 * the session can produce now, the figure (PNG at 300 dpi and SVG), the
 * research record when one is recording, a README saying what each file
 * is, and `manifest.json` with the SHA-256 of every file.
 *
 * File names inside the zip are fixed (`data/pins.csv`, not the dated name
 * the browser saves a single export under), so a script can find them in
 * any bundle; the zip's own name carries the dataset and the day. Only the
 * parts that exist go in: no empty CSV, no figure when there was nothing
 * to draw. The manifest lists every file except itself; the README is in
 * it, so a changed README shows.
 *
 * Pure apart from the zip writer (JSZip, the one the lessons and the
 * research record use) and Web Crypto for the hashes.
 */
import { sha256Hex } from '../khipuIntegrity'
import { AYNI_XR_PUBLISHER, citationBibtex, citationRis, citationText, formatFrameTime, type CitationInput } from './citation'

/** One file in the bundle. */
export interface BundleFile {
  /** Its path in the zip (`data/pins.csv`): fixed, so scripts can find it. */
  readonly path: string
  readonly body: string | Uint8Array
  /** What it is, in a few words, for README.txt and the manifest. */
  readonly about: string
}

/** The CSV files a session can hold, by their fixed path, in the order the README lists them. */
export const BUNDLE_CSV = {
  statistics: 'data/statistics.csv',
  latitude: 'data/latitude-profile.csv',
  line: 'data/line-profile.csv',
  hovmoller: 'data/hovmoller.csv',
  pins: 'data/pins.csv',
  pinStatistics: 'data/pin-statistics.csv',
  compare: 'data/compare.csv',
  threshold: 'data/threshold.csv',
  regionSeries: 'data/region-through-time.csv',
} as const

export type BundleCsvKind = keyof typeof BUNDLE_CSV

/** What each CSV is, for the README and the manifest. */
const CSV_ABOUT: Record<BundleCsvKind, string> = {
  statistics: 'Statistics over the region in Analyze: area-weighted mean, median, percentiles, spread and histogram, with the baseline and its differences when one is held',
  latitude: 'Latitude profile: the mean of each grid row over the same region',
  line: 'Line profile: the values along the line drawn on the globe, point by point',
  hovmoller: 'Line over time (Hovmöller diagram): one row per frame read, one column per point along the line',
  pins: "Pins: each pin's value at every frame of the window (blank: not read yet), with the trend, emergence and correlation tables after them when those are on",
  pinStatistics: "Pin statistics alone: trend, time of emergence and correlation, as shown under the pins' graph",
  compare: 'Compare: the dataset minus another (an overlay or another globe), with model-evaluation statistics and the region side by side',
  threshold: 'Area over a threshold, frame by frame',
  regionSeries: 'A region through time: its mean, highest and lowest in every frame, and when the records were',
}

/** A CSV the session can produce now. */
export interface BundleCsv {
  readonly kind: BundleCsvKind
  readonly text: string
}

export interface BundleInput {
  readonly citation: CitationInput
  /** The methods paragraph and its references (`methods.methodsText`); null when there is nothing to describe. */
  readonly methods: string | null
  readonly csvs: readonly BundleCsv[]
  /** The figure as exported (PNG with its 300 dpi inside, and SVG), and how it was drawn. */
  readonly figure: { readonly png: Uint8Array; readonly svg: string; readonly note: string } | null
  /** The research record of the session, when one is recording (`.kpu` JSON). */
  readonly record: string | null
  readonly createdAt: Date
}

export const BUNDLE_FORMAT = 'ayni-xr-reproducibility-bundle'
export const BUNDLE_VERSION = 1

/** The parts in the order the README lists them; only those that exist. */
export function bundleParts(input: BundleInput): BundleFile[] {
  const c = input.citation
  const files: BundleFile[] = [
    { path: 'citation/citation.txt', body: `${citationText(c)}\n`, about: 'The citation of the view (APA-like text)' },
    { path: 'citation/citation.bib', body: citationBibtex(c), about: 'The same citation as BibTeX (LaTeX)' },
    { path: 'citation/citation.ris', body: citationRis(c), about: 'The same citation as RIS (Zotero, Mendeley, EndNote)' },
    { path: 'view-link.txt', body: `${c.viewUrl}\n`, about: 'The link that reopens the view: the dataset, the frame, the camera and the layers' },
  ]
  if (input.methods) files.push({ path: 'methods.txt', body: `${input.methods.trim()}\n`, about: 'The methods paragraph, written from what was done in this session, with its references' })
  const seen = new Set<BundleCsvKind>()
  for (const kind of Object.keys(BUNDLE_CSV) as BundleCsvKind[]) {
    const csv = input.csvs.find((x) => x.kind === kind)
    if (!csv || seen.has(kind) || !csv.text.trim()) continue
    seen.add(kind)
    files.push({ path: BUNDLE_CSV[kind], body: csv.text, about: CSV_ABOUT[kind] })
  }
  if (input.figure) {
    files.push({ path: 'figure/figure.png', body: input.figure.png, about: `The figure, PNG at 300 dpi (its title and caption are inside the file). ${input.figure.note}` })
    files.push({ path: 'figure/figure.svg', body: input.figure.svg, about: 'The same figure as SVG: title, colour bar, keys, pins and caption as text and shapes; the map is a picture' })
  }
  if (input.record) files.push({ path: 'record/research-record.kpu', body: input.record, about: 'The research record of this session (AYNI Khipu, JSON): the views in order with their links and citations, the numbers read, and the files exported with their SHA-256' })
  return files
}

/** Each source with its licence and page, once, in the publisher's order. */
function sourceLines(c: CitationInput): string[] {
  const out: string[] = []
  for (const s of c.credit?.sources ?? []) {
    const licence = s.licenseName ?? s.licenseUrl ?? 'licence not stated by the publisher'
    const line = `- ${s.label ?? 'Source'}: ${licence}${s.licenseUrl && s.licenseName ? ` (${s.licenseUrl})` : ''}${s.sourcePage ? `. ${s.sourcePage}` : ''}`
    if (!out.includes(line)) out.push(line)
  }
  return out
}

/** README.txt: what each file is, how to reopen the view and check the files, the licences, the software. */
export function bundleReadme(input: BundleInput, files: readonly BundleFile[]): string {
  const c = input.citation
  const doi = c.softwareDoi?.trim()
  const width = Math.max(...files.map((f) => f.path.length), 'manifest.json'.length)
  const frame = c.frameMs !== null ? formatFrameTime(c.frameMs, c.framePrecision) : null
  const sources = sourceLines(c)
  const credit = c.credit?.text.trim()
  return [
    `AYNI XR reproducibility bundle: ${c.title}`,
    '',
    `Made on ${input.createdAt.toISOString().slice(0, 16).replace('T', ' ')} UTC with ${c.software} (${AYNI_XR_PUBLISHER}${doi ? `, https://doi.org/${doi}` : ''}).`,
    `Dataset: ${c.title} (${c.datasetId})${frame ? `, frame ${frame}` : ''}.`,
    '',
    'The files',
    '',
    ...files.map((f) => `${f.path.padEnd(width)}  ${f.about}`),
    `${'manifest.json'.padEnd(width)}  Every file above with its size and SHA-256`,
    `${'README.txt'.padEnd(width)}  This file (listed in the manifest too)`,
    '',
    'Only what this session had when the bundle was made is here: a CSV is missing when that',
    'analysis was not on screen, the figure when there was nothing to draw, the record when no',
    'research record was recording.',
    '',
    'Reopening the view',
    '',
    `Open the link in view-link.txt in a browser: ${c.viewUrl}`,
    'It opens the same dataset paused on the same frame, with the camera and the layers as they',
    'were. Opening it needs an AYNI XR plan that includes the dataset.',
    '',
    'Checking the files',
    '',
    'manifest.json gives the SHA-256 of every file. On Linux or macOS: shasum -a 256 <file>;',
    'on Windows: certutil -hashfile <file> SHA256. A file whose hash differs has been changed.',
    input.record
      ? 'The research record (record/research-record.kpu) carries its own integrity block; its README in\nthe record download explains how to check it.'
      : '',
    '',
    'Licences',
    '',
    'The data keeps the licence its publisher gives it; cite it as the citation says.',
    ...(credit ? [`Credit: ${credit}`] : []),
    ...(sources.length ? sources : ['- The publisher states no licence for this dataset.']),
    `The CSV files, the figure and the methods text describe that data; the same licence and credit apply`,
    'to anything made from them.',
    `Cite AYNI XR as: AYNI XR (${AYNI_XR_PUBLISHER})${doi ? `, https://doi.org/${doi}` : ''}.`,
    'The AYNI Khipu format of the research record is proprietary to Pachamama Studios B.V.',
    '',
  ].filter((line, i, all) => !(line === '' && all[i - 1] === '')).join('\n')
}

/** manifest.json: the bundle's facts and every file with its size and SHA-256. */
export async function bundleManifest(input: BundleInput, files: readonly BundleFile[]): Promise<string> {
  const c = input.citation
  const listed = []
  for (const f of files) {
    const bytes = typeof f.body === 'string' ? new TextEncoder().encode(f.body) : f.body
    listed.push({ path: f.path, bytes: bytes.length, sha256: await sha256Hex(bytes), about: f.about })
  }
  const manifest = {
    format: BUNDLE_FORMAT,
    version: BUNDLE_VERSION,
    createdAt: input.createdAt.toISOString(),
    software: c.software,
    softwareDoi: c.softwareDoi ?? null,
    publisher: AYNI_XR_PUBLISHER,
    dataset: { id: c.datasetId, title: c.title },
    frame: c.frameMs !== null ? formatFrameTime(c.frameMs, c.framePrecision) : null,
    viewUrl: c.viewUrl,
    hash: 'SHA-256',
    files: listed,
  }
  return `${JSON.stringify(manifest, null, 2)}\n`
}

/** Every file of the bundle, README and manifest included, in zip order. */
export async function bundleFiles(input: BundleInput): Promise<BundleFile[]> {
  const parts = bundleParts(input)
  const readme: BundleFile = { path: 'README.txt', body: '', about: 'What each file is, how to reopen the view and check the files, the licences' }
  const withReadme = [{ ...readme, body: bundleReadme(input, parts) }, ...parts]
  const manifest: BundleFile = { path: 'manifest.json', body: await bundleManifest(input, withReadme), about: 'Every file with its size and SHA-256' }
  return [withReadme[0], manifest, ...parts]
}

/** The bundle as one zip (every entry dated when it was made), its name and its own SHA-256. */
export async function zipBundle(files: readonly BundleFile[], name: string, createdAt: Date): Promise<{ blob: Blob; filename: string; sha256: string }> {
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  for (const f of files) zip.file(f.path, f.body, { date: createdAt })
  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/zip', compression: 'DEFLATE' })
  return { blob, filename: name, sha256: await sha256Hex(blob) }
}
